import { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  CircularProgress,
  Collapse,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Button,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CompletedIcon,
  Error as FailedIcon,
  HourglassEmpty as PendingIcon,
  Autorenew as RunningIcon,
  Recommend as RecsIcon,
} from '@mui/icons-material';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSiteStore } from '@/store/site.store';
import {
  listSiteAuditRuns,
  getSiteAuditPages,
  listPageAuditRequests,
  type SiteAuditRun,
  type SiteAuditPage,
  type PageAuditRequest,
} from '@/services/customer/auditApi';

// ─── helpers ──────────────────────────────────────────────────────────────────

const SITE_STATUS_COLOR: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  PENDING: 'default', DISPATCHING: 'info', RUNNING: 'warning', COMPLETED: 'success', FAILED: 'error',
};

const SITE_STATUS_ICON: Record<string, React.ReactNode> = {
  PENDING:     <PendingIcon fontSize="small" />,
  DISPATCHING: <RunningIcon fontSize="small" />,
  RUNNING:     <RunningIcon fontSize="small" sx={{ animation: 'spin 1.5s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />,
  COMPLETED:   <CompletedIcon fontSize="small" />,
  FAILED:      <FailedIcon fontSize="small" />,
};

const PAGE_STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'error'> = {
  QUEUED: 'default', PROCESSING: 'info', COMPLETED: 'success', FAILED: 'error', CANCELLED: 'default',
};

const fmt = (d?: string | null) =>
  d ? new Date(d.endsWith('Z') ? d : d + 'Z').toLocaleString() : '—';

const progressPct = (r: SiteAuditRun) =>
  r.totalPages > 0 ? Math.round(((r.completedPages + r.failedPages) / r.totalPages) * 100) : 0;

const isActiveSite = (r: SiteAuditRun) =>
  r.status === 'PENDING' || r.status === 'DISPATCHING' || r.status === 'RUNNING';

const PAGE_SIZE = 15;

// ─── Pages inline detail ──────────────────────────────────────────────────────

function PagesDetail({ run }: { run: SiteAuditRun }) {
  const { data, isLoading } = useQuery({
    queryKey: ['site-audit-pages', run.id],
    queryFn: () => getSiteAuditPages(run.id),
    refetchInterval: isActiveSite(run) ? 8000 : false,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  const pages: SiteAuditPage[] = data?.data ?? [];

  return (
    <Table size="small" sx={{ bgcolor: 'grey.50' }}>
      <TableHead>
        <TableRow>
          <TableCell>Page URL</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Recommendations</TableCell>
          <TableCell>Completed</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {pages.length === 0 ? (
          <TableRow>
            <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 2 }}>
              No pages yet
            </TableCell>
          </TableRow>
        ) : (
          pages.map((p) => (
            <TableRow key={p.requestId}>
              <TableCell sx={{ maxWidth: 300 }}>
                <Tooltip title={p.pageUrl}>
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: 'monospace', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {p.pageUrl}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Chip
                  label={p.status}
                  size="small"
                  color={p.status === 'COMPLETED' ? 'success' : p.status === 'FAILED' ? 'error' : 'default'}
                />
              </TableCell>
              <TableCell>
                {p.recommendationCount > 0 ? (
                  <Chip label={p.recommendationCount} size="small" color="primary" variant="outlined" />
                ) : '—'}
              </TableCell>
              <TableCell sx={{ fontSize: '0.75rem' }}>{fmt(p.completedAt)}</TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

// ─── Tab 0: Site Audits ───────────────────────────────────────────────────────

function SiteAuditsTab() {
  const { selectedSite } = useSiteStore();
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const toggle = useCallback((id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['site-audit-runs', selectedSite?.id],
    queryFn: () => listSiteAuditRuns({ siteId: selectedSite?.id, size: 30 }),
    enabled: !!selectedSite?.id,
    refetchInterval: (query) => {
      const runs = query.state.data?.data ?? [];
      return runs.some(isActiveSite) ? 10_000 : false;
    },
  });

  const runs: SiteAuditRun[] = data?.data ?? [];

  if (!selectedSite) {
    return <Alert severity="info" sx={{ mt: 2 }}>Select a site from the top bar to view audit runs.</Alert>;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? <CircularProgress size={18} /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{(error as Error).message}</Alert>}

      <Card variant="outlined">
        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.default' }}>
                <TableCell sx={{ width: 40 }} />
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Progress</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Pages</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>AI</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Started</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Completed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : runs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No audit runs yet —{' '}
                    <Link to="/customer/audit/site">Start a Site Audit</Link>.
                  </TableCell>
                </TableRow>
              ) : (
                runs.map((run) => (
                  <>
                    <TableRow
                      key={run.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => toggle(run.id)}
                    >
                      <TableCell padding="checkbox">
                        <IconButton size="small">
                          {expanded.has(run.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={SITE_STATUS_ICON[run.status] as React.ReactElement}
                          label={run.status}
                          color={SITE_STATUS_COLOR[run.status] ?? 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 160 }}>
                        {isActiveSite(run) ? (
                          <Box>
                            <LinearProgress variant="determinate" value={progressPct(run)} sx={{ mb: 0.5, borderRadius: 4 }} />
                            <Typography variant="caption" color="text.secondary">
                              {run.completedPages}/{run.totalPages > 0 ? run.totalPages : '?'} pages
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2">
                            {run.completedPages}/{run.totalPages} pages
                            {run.failedPages > 0 && (
                              <Typography component="span" variant="caption" color="error.main" sx={{ ml: 0.5 }}>
                                ({run.failedPages} failed)
                              </Typography>
                            )}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{run.totalPages > 0 ? run.totalPages : '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={run.llmEnabled ? 'AI On' : 'Off'}
                          size="small"
                          variant="outlined"
                          color={run.llmEnabled ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        {fmt(run.startedAt ?? run.createdAt)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        {fmt(run.completedAt)}
                      </TableCell>
                    </TableRow>

                    <TableRow key={`pages-${run.id}`}>
                      <TableCell
                        colSpan={7}
                        sx={{ p: 0, borderBottom: expanded.has(run.id) ? undefined : 'none' }}
                      >
                        <Collapse in={expanded.has(run.id)} unmountOnExit>
                          <Box sx={{ p: 1 }}>
                            {run.errorMessage && (
                              <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
                                {run.errorMessage}
                              </Alert>
                            )}
                            <PagesDetail run={run} />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}

// ─── Tab 1: Page Audits ───────────────────────────────────────────────────────

function PageAuditsTab() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['page-audit-requests', statusFilter, page],
    queryFn: () => listPageAuditRequests({ status: statusFilter || undefined, page, size: PAGE_SIZE }),
    staleTime: 30_000,
  });

  const rows: PageAuditRequest[] = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="QUEUED">Queued</MenuItem>
            <MenuItem value="PROCESSING">Processing</MenuItem>
            <MenuItem value="COMPLETED">Completed</MenuItem>
            <MenuItem value="FAILED">Failed</MenuItem>
          </Select>
        </FormControl>
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? <CircularProgress size={18} /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{(error as Error).message}</Alert>}

      <Card variant="outlined">
        <TableContainer component={Paper} elevation={0}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.default' }}>
                <TableCell sx={{ fontWeight: 600 }}>Page</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>AI</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Recommendations</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Submitted</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Completed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No page audit requests yet —{' '}
                    <Link to="/customer/audit/page">Submit a Page Audit</Link>.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ maxWidth: 260 }}>
                      {row.page_url ? (
                        <Tooltip title={row.page_url}>
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: 240 }}
                          >
                            {row.page_url}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Chip label="Full-site" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.status}
                        size="small"
                        color={PAGE_STATUS_COLOR[row.status] ?? 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.llm_enabled ? 'On' : 'Off'}
                        size="small"
                        variant="outlined"
                        color={row.llm_enabled ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {row.recommendation_count != null && row.recommendation_count > 0 ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<RecsIcon fontSize="small" />}
                          onClick={() =>
                            navigate(
                              `/customer/audit/recommendations?runId=${encodeURIComponent(row.run_id ?? '')}&requestId=${row.id}`
                            )
                          }
                          disabled={!row.run_id}
                        >
                          {row.recommendation_count} Recs
                        </Button>
                      ) : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                      {fmt(row.created_at)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                      {fmt(row.completed_at)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {total > PAGE_SIZE && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5 }}>
            <Typography variant="caption" color="text.secondary">{total} requests total</Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, p) => setPage(p)}
              size="small"
              color="primary"
            />
          </Box>
        )}
      </Card>
    </Box>
  );
}

// ─── main page ────────────────────────────────────────────────────────────────

export default function CustomerAuditTrackingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === '1' ? 1 : 0;

  const handleTabChange = (_: React.SyntheticEvent, value: number) => {
    setSearchParams(value === 1 ? { tab: '1' } : {}, { replace: true });
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Audit History</Typography>
        <Typography variant="body2" color="text.secondary">
          Track the status of your site and page audit requests.
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={handleTabChange}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Site Audits" />
        <Tab label="Page Audits" />
      </Tabs>

      {tab === 0 && <SiteAuditsTab />}
      {tab === 1 && <PageAuditsTab />}
    </Box>
  );
}
